import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useMutation } from '@apollo/react-hooks';
import Button from './Button';
import Card from './Card';
import BaseModal, { ModalList } from './modals/BaseModal';
import colors from '../styles/colors';
import { UserContext } from '../context/UserContext';
import { DELETE_CONTACT_TAGS } from '../graphql/queries';
import NoContent from '../shared/NoContent';

const Wrapper = styled.div`
  width: 80%;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const Content = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 0 5%;
`;

const TagsHeader = styled.div`
  font-size: 1.2rem;
  color: ${colors.grey3};
  margin: 30px 0px 10px 0px;
`;

const Header = styled.div`
  font-size: 1.2rem;
  color: ${colors.grey3};
  margin: 0px 0px 0px 0px;
  border-bottom: 1px solid #e0dfdf;
  padding: 0 30px;
`;

const TagsContainer = styled.div`
  font-size: 1.3rem;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
`;

const ToolbarItem = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.2s;
  font-weight: ${(props) => (props.active ? '600' : '500')};
  color: ${(props) => (props.active ? colors.seaGreen : colors.grey3)};
  margin: 7px;
  font-size: 1.4rem;
  width: max-content;
  border-radius: 10px;
  padding: 5px 10px;
  cursor: ${(props) => (props.onClick ? 'pointer' : null)};
  background: ${(props) => (props.active ? 'rgba(26, 188, 156, 0.1)' : null)};
  word-wrap: break-word;
  word-break: break-all;
`;

const CrossButton = styled.div`
  margin-left: 5px;
  padding: 3px 7px;
  border-radius: 50%;
  cursor: pointer;
  &:hover {
    color: red;
    background-color: rgb(189, 195, 199);
  }
`;

export default function Tags() {
  const { user, setUser } = useContext(UserContext);
  const [modalIsVisible, setModalIsVisible] = useState(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [tagToDelete, setTagToDelete] = useState('');
  const [tags, setTags] = useState([]);
  const [deleteContactTag, { loading: deleteTagLoading }] = useMutation(
    DELETE_CONTACT_TAGS,
    {
      onCompleted(data) {
        setDeleteConfirmVisible(false);
        toast.success(`Tag '${tagToDelete}' Deleted Successfully`);
        const updatedUser = { ...user };
        updatedUser.organization.tags = data.removeOrgTag.tags;
        setUser(() => updatedUser);
      },
    },
  );

  const deleteClick = (tag) => {
    deleteContactTag({
      variables: {
        tag,
      },
    });
  };

  useEffect(() => {
    if (user?.organization?.tags) {
      const tagsData = user?.organization?.tags;
      setTags(tagsData);
    }
  }, [user]);

  return (
    <Wrapper>
      <Card width="100%" padding="20px 0">
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <BaseModal
            open={modalIsVisible}
            modalType={ModalList.AddContactTag}
            onClose={setModalIsVisible}
            setTags={setTags}
          />
          <BaseModal
            open={deleteConfirmVisible}
            modalType={ModalList.DeleteContactTag}
            onClose={setDeleteConfirmVisible}
            onConfirm={() => {
              deleteClick(tagToDelete);
            }}
            loading={deleteTagLoading}
            header="Deleting this tag will remove it from all contacts that have been tagged. Are you sure?"
          />
          <Header>
            <Button
              margin="0 0 10px 0"
              text="ADD NEW TAG"
              type="button"
              width="fit-content"
              onClick={() => setModalIsVisible(true)}
            />
          </Header>
          {tags.length ? (
            <Content>
              <TagsHeader>Tags</TagsHeader>
              <TagsContainer>
                {tags.map((item, index) => {
                  return (
                    <ToolbarItem
                      active={true}
                      key={index}
                      headerDirection="column"
                    >
                      {item}
                      <CrossButton
                        onClick={() => {
                          setDeleteConfirmVisible(true);
                          setTagToDelete(item);
                        }}
                      >
                        x
                      </CrossButton>
                    </ToolbarItem>
                  );
                })}
              </TagsContainer>
            </Content>
          ) : (
            <NoContent
              header="No tags found"
              description="Your organization do'nt have any tags yet."
            />
          )}
        </div>
      </Card>
    </Wrapper>
  );
}
